import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SiteDropdownItem } from 'app/_models/site-dropdown-item.model';
import { SiteSummary } from 'app/_models/site-summary.model';
import { Observable } from 'rxjs/Observable';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseHttpClientService } from './base-http-client.service';

@Injectable()
export class SiteService extends BaseHttpClientService {

  constructor(
    private http: HttpClient,
  ) {
    super()
  }

  // a flat list of sites the current user is allowed to see with minimal data
  public getSiteListSummary(): Observable<SiteSummary[]> {
    return this.http.get(environment.apiUrl + 'api/ext-site/summary')
      .pipe(map((response) => (response as any[]).map((site) => new SiteSummary().deserialize(site))))
      .pipe(catchError(this.handleError));
  }

  // Every site the current user can reach, carrying only what the dropdown screens read
  public getAllSitesNoPagination(): Observable<SiteDropdownItem[]> {
    return this.http.get(environment.apiUrl + 'api/ext-site/for-dropdowns')
      .pipe(map((response) => (response as any[]).map((site) => new SiteDropdownItem().deserialize(site))))
      .pipe(catchError(this.handleError));
  }

  // Get all sites w/pagination, sorting and filters: slim with six fields per row
  public getAllSites(pagination = { size: 99999, page: 0 }, sortArray: string[] = ['name,asc'], filters: { name?: string, owner?: string } = {}) {
    let sortString = sortArray.map(sort => 'sort=' + sort).join('&');
    let paginationString = `size=${pagination.size}&page=${pagination.page}`;
    let filterString = '';

    if (filters.name) {
      filterString += `&name=${encodeURIComponent(filters.name)}`;
    }

    if (filters.owner) {
      filterString += `&owner=${encodeURIComponent(filters.owner)}`;
    }

    let apiUrl = environment.apiUrl + 'api/ext-site/search/list?';
    let fullUrl = `${apiUrl}${paginationString}&${sortString}${filterString}`;

    const headers = this.applyLatestOnlyHeader();

    return this.http.get(fullUrl, { observe: 'response', headers })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  public getDashboardSummaryBySite(): Observable<any> {
    return this.http
      .get(environment.apiUrl + 'api/ext-site/dashboard-summary/')
      .pipe(catchError(this.handleError));
  }


  // Get Site by Id
  public getSiteById(id) {
    return this.http.get(environment.apiUrl + 'api/ext-site/' + id)
      .pipe(catchError(this.handleError));
  }

  // CREATE SITE
  public createSite(body) {
    return this.http.post(environment.apiUrl + 'api/ext-site/create', body)
      .pipe(catchError(this.handleError));
  }

  // EDITING SITE //

  // PUT requests need every value defined even if they aren't being updated.
  // This method creates the object with the old values, then updates the new values
  public createRequestBodyForEditingSite(selectedSite: any, newValues: any = {}): any {
    const allowedFields = [
      'id',
      'name',
      'mainContactNumber',
      'emergencyContactNumber',
      'altEmergencyContactNumber',
      'description',
      'healthCheckInterval',
      'addAssetCompetencies',
      'latitude',
      'longitude',
      'geofence'
    ];

    const requestBody = {};

    allowedFields.forEach(field => {
      if (newValues[field] !== undefined) {
        requestBody[field] = newValues[field];
      } else if (selectedSite[field] !== undefined) {
        requestBody[field] = selectedSite[field];
      }
    });

    requestBody['addInduction'] = true; // todo remove addInduction from db - shouldn't be needed with new course data structures

    // Check for any fields in formValues that are not in allowedFields
    // This is to alert developers when a new field is added in one place but missing here
    const extraFields = Object.keys(newValues).filter(field => !allowedFields.includes(field));
    if (extraFields.length > 0) {
      throw new Error(`The following fields are missing in SiteService: ${extraFields.join(', ')}`);
    }

    return requestBody;
  }

  public editSiteDetails(obj) {
    return this.http.put(environment.apiUrl + 'api/ext-site', obj)
      .pipe(catchError(this.handleError));
  }

  public updateSiteOwner(obj) {
    return this.http.post(environment.apiUrl + 'api/ext-site/update-site-owner', obj)
      .pipe(catchError(this.handleError));
  }

  // Delete
  public deleteSite(id) {
    return this.http.delete(environment.apiUrl + 'api/ext-site/' + id)
      .pipe(catchError(this.handleError));
  }

  // Worker: the current worker's sites, as slim rows at the same paths. Sorts on site.name,
  // site.siteType.name and site.siteOwnerUser.skylarkUser.user.firstName/lastName
  public getAllSitesForWorker(pagination, justForSchedule = false, sortArray: string[] = ['site.name,asc']) {
    let params = new HttpParams();

    sortArray.forEach(sort => params = params.append('sort', sort));
    params = params.append('page', String(pagination.page));
    params = params.append('size', String(pagination.size));
    params = params.append('assetScheduleSites', String(justForSchedule));

    return this.http.get(environment.apiUrl + 'api/ext-worker-user-site/by-current-user/', { params, observe: 'response' })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  public getWorkerUserSiteForCurrentUserAndSite(siteId) {
    return this.http.get(environment.apiUrl + 'api/ext-worker-user-site/by-current-user/by-site/' + siteId)
      .pipe(catchError(this.handleError));
  }

  // Site delegate Manage Sites: the delegate's delegate sites, or their other sites. Takes the same sort keys
  // as getAllSitesForWorker, and a partial site name
  public getAllSitesForSiteDelegate(pagination, delegateOnlySites: boolean, sortArray: string[] = ['site.name,asc'],
                                    filters: { name?: string } = {}) {
    let params = new HttpParams()
      .append('size', String(pagination.size))
      .append('page', String(pagination.page))
      .append('delegateOnlySites', String(delegateOnlySites));

    sortArray.forEach(sort => params = params.append('sort', sort));
    if (filters.name) {
      params = params.append('name', filters.name);
    }

    return this.http.get(environment.apiUrl + 'api/ext-site/current-site-delegate', { params, observe: 'response' })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  public addSiteRestriction(restriction) {
    return this.http.post(environment.apiUrl + 'api/ext-restriction/' , restriction)
      .pipe(catchError(this.handleError));
  }

  public removeSiteRestriction(restrictionId: number) {
    return this.http.put(environment.apiUrl + 'api/ext-restriction/' + restrictionId + '/in-activate', {})
      .pipe(catchError(this.handleError));
  }

  public getRestrictionsReport(pagination, sortproperty, sortdirection, from, to, site, restrictionType) {
    let params = new HttpParams();
    params = params.append('startDate', from);
    params = params.append('endDate', to);
    params = params.append('sort', sortproperty + ',' + sortdirection);
    params = params.append('page', String(pagination.page));
    params = params.append('size', String(pagination.size));
    if(site){
      params = params.append('siteId', site);
    }
    if(restrictionType){
      params = params.append('type', restrictionType);
    }

    const headers = this.applyLatestOnlyHeader();

    return this.http.get(environment.apiUrl + 'api/ext-restriction/report', { params, observe: 'response', headers })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  public addDeleteOEMRelationShip(siteId: number, maintenanceId: number, deleteRelationship: boolean) {
    const uri = environment.apiUrl + 'api/ext-site/' + siteId + '/oem/' + maintenanceId;
    const request = deleteRelationship ? this.http.delete(uri, {observe: 'response'}) : this.http.post(uri, {observe: 'response'});

    return request
      .pipe(catchError(this.handleError));
  }

  public updateSiteOemProcedureSettings(settings, siteId) {
    return this.http.put(environment.apiUrl + 'api/ext-site/' + siteId + "/oem-settings", settings)
      .pipe(catchError(this.handleError));
  }

  public handleDataWithHeaders(res: HttpResponse<any>) {
    const headers = res.headers;
    const body = res.body;
    const data = {
      'headers': headers,
      'body': body
    };
    return data;
  }
}
